import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router";
import type { ProductSummary } from "@jipraks/shared";
import { buttonClassName } from "@client/components/ui/button-variants";
import { SpotlightCard } from "@client/components/ui/SpotlightCard";
import { cn } from "@client/lib/cn";
import { authorsCopy } from "@client/locales/id/authors";
import { catalogCopy } from "@client/locales/id/catalog";
import { PriceTag } from "./PriceTag";
import { ProductCardMeta } from "./ProductCardMeta";
import { RecommendedBadge } from "./RecommendedBadge";

const MAX_BENEFITS = 3;

interface FeaturedProductCardProps {
  product: ProductSummary;
  /** Spans the full row: image beside the text from `md` up instead of above it. */
  isWide?: boolean;
}

/** A larger, accent-edged product card that sells with benefits and an explicit CTA. */
export function FeaturedProductCard({ product, isWide = false }: FeaturedProductCardProps) {
  const benefits = product.benefits.slice(0, MAX_BENEFITS);

  return (
    <SpotlightCard variant="accent" className="h-full">
      <Link
        to={`/course/${product.slug}`}
        className={cn("flex h-full flex-col", isWide && "md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]")}
      >
        <div
          className={cn(
            "relative aspect-video overflow-hidden border-b border-border-default bg-background-elevated",
            isWide && "md:aspect-auto md:min-h-72 md:border-r md:border-b-0",
          )}
        >
          {product.imageUrl && isWide && (
            // The wide cell is taller than 16:9; covers carry text, so show the whole image over a
            // blurred copy of itself instead of cropping it. Same URL, so no extra request.
            <img
              src={product.imageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 hidden size-full scale-110 object-cover opacity-40 blur-2xl md:block"
            />
          )}
          {product.imageUrl && (
            <img
              src={product.imageUrl}
              alt={product.title}
              width={960}
              height={540}
              className={cn(
                "relative size-full object-cover transition-transform duration-300 ease-expo-out group-hover:scale-[1.02]",
                isWide && "md:absolute md:inset-0 md:object-contain",
              )}
            />
          )}
          <RecommendedBadge />
        </div>

        <div className="flex flex-1 flex-col gap-4 p-6 lg:p-8">
          <ProductCardMeta product={product} />
          <h3
            className={cn(
              "text-xl leading-snug font-semibold tracking-tight text-foreground md:text-2xl",
              isWide && "lg:text-3xl",
            )}
          >
            {product.title}
          </h3>
          {product.author && (
            <p className="truncate text-sm text-foreground-muted">
              {authorsCopy.by} <span className="text-foreground">{product.author.name}</span>
            </p>
          )}
          {product.shortDescription && (
            <p className="line-clamp-3 leading-relaxed text-foreground-muted">{product.shortDescription}</p>
          )}
          {benefits.length > 0 && (
            <ul className="flex flex-col gap-2">
              {benefits.map((benefit) => (
                <li key={benefit} className="flex items-start gap-2.5 text-sm text-foreground">
                  <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
                  <span className="line-clamp-2">{benefit}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-2">
            <PriceTag price={product.price} normalPrice={product.normalPrice} size={isWide ? "lg" : "md"} />
            {/* Styled as a button, but a span: the whole card is the link. */}
            <span className={buttonClassName("primary", "md")}>
              {catalogCopy.viewDetail}
              <ArrowRight
                className="size-4 transition-transform duration-200 ease-expo-out group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </span>
          </div>
        </div>
      </Link>
    </SpotlightCard>
  );
}
